import { Link } from "react-router-dom";
import { Wallet, ArrowRight, Loader2, Plus, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMyWallets } from "@/hooks/use-queries";
import { resolveAvatarUrl } from "@/lib/utils";
import { api } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const walletStatusVariant: Record<string, "default" | "secondary" | "destructive"> = {
  ACTIVE: "default",
  PROVISIONING: "secondary",
  SUSPENDED: "destructive",
};

export default function WalletsOverview() {
  const { data: wallets, isLoading } = useMyWallets();
  const queryClient = useQueryClient();

  const provisionMutation = useMutation({
    mutationFn: async (agentId: string) => {
      const { data } = await api.post(`/agents/${agentId}/wallet/provision`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-wallets"] });
      toast.success("Wallet provisioning started");
    },
    onError: () => toast.error("Failed to provision wallet"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalWithWallets = wallets?.filter((w) => w.wallet).length ?? 0;
  const totalAgents = wallets?.length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display">Fleet Wallets</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {totalWithWallets} of {totalAgents} agents have wallets provisioned
          </p>
        </div>
        <Link to="/agents/new">
          <Button variant="outline" size="sm">
            <Plus className="size-4 mr-2" />
            New Agent
          </Button>
        </Link>
      </div>

      {!wallets?.length ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <Wallet className="mx-auto h-10 w-10 mb-3 opacity-40" />
            <p className="font-medium">No agents yet</p>
            <p className="text-sm mt-1">Register an agent to get started.</p>
            <Link to="/agents/new">
              <Button className="mt-4" size="sm">Register Agent</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {wallets.map((entry: any) => (
            <Card key={entry.agentId} className="flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <Avatar className="size-10">
                    <AvatarImage src={resolveAvatarUrl(entry.agentAvatarUrl)} />
                    <AvatarFallback className="text-xs bg-primary/10 text-primary uppercase">
                      {entry.agentName?.substring(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{entry.agentName}</p>
                    <p className="text-xs text-muted-foreground font-mono truncate">@{entry.agentId}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 flex flex-col gap-3 flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Agent status</span>
                  <Badge variant={entry.agentStatus === "ACTIVE" ? "default" : "secondary"} className="text-xs">
                    {entry.agentStatus}
                  </Badge>
                </div>

                {entry.wallet ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Wallet</span>
                      <Badge variant={walletStatusVariant[entry.wallet.status] ?? "secondary"} className="text-xs">
                        {entry.wallet.status}
                      </Badge>
                    </div>
                    <div className="bg-muted/50 rounded-md px-2.5 py-2">
                      <p className="text-[10px] text-muted-foreground font-mono break-all">{entry.wallet.address}</p>
                    </div>
                    <Link to={`/agents/${entry.agentId}/wallet`} className="mt-auto">
                      <Button variant="outline" size="sm" className="w-full">
                        View Wallet
                        <ArrowRight className="size-3.5 ml-2" />
                      </Button>
                    </Link>
                  </>
                ) : (
                  <div className="mt-auto">
                    <p className="text-xs text-muted-foreground mb-3">No wallet provisioned</p>
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() => provisionMutation.mutate(entry.agentId)}
                      disabled={provisionMutation.isPending}
                    >
                      {provisionMutation.isPending ? (
                        <Loader2 className="size-3.5 animate-spin mr-2" />
                      ) : (
                        <Wallet className="size-3.5 mr-2" />
                      )}
                      Provision Wallet
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
